/**
 * MODULE: _shared/frontend-shell/src/features/crm/hooks/useAccountDrawer.ts
 * PURPOSE: Load CRM hub account for AccountDrawer, map to editable model, optimistic field saves after parent onSave
 * DEPENDENCIES: react, @shared/lib/frontend/api/crmApi, @shared/lib/frontend/api/accountApi (contacts only), ../data/opportunityDrawerOptions, ../data/scheduleModalCatalog
 * PUBLIC_API: useAccountDrawer, AccountDrawerModel, CRM_NEW_ACCOUNT_ID, blankAccountDrawerModel, patchAccount via hook result
 * SIDE_EFFECTS: crmGet network call (skipped for create sentinel); accountApi.getContacts for mini contact list
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: 275/300
 * RECENT: 2026-05-25 — Load/patch via CRM hub API (`crmGet`); `isArchived` on model for archive/restore UX (ARCHITECTURE.md CRM canonical data model [2.2.1191]).
 * RECENT_PRIOR: 2026-05-01 **`CRM_NEW_ACCOUNT_ID`** create mode (blank model, no GET) — CHANGELOG [2.2.780]
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { accountApi } from '@shared/lib/frontend/api/accountApi';
import { crmGet, type CrmHubAccount } from '@shared/lib/frontend/api/crmApi';
import { MOCK_SCHEDULE_OWNERS } from '../data/scheduleModalCatalog';

/** Sentinel id — AccountDrawer create mode; not a persisted account id. */
export const CRM_NEW_ACCOUNT_ID = '__new__';

export type AccountTypeOption = 'customer' | 'prospect' | 'partner';

export type AccountDrawerModel = {
  id: string;
  name: string;
  industry: string;
  website: string;
  phone: string;
  street: string;
  city: string;
  stateOrCounty: string;
  country: string;
  postcode: string;
  territoryId: string;
  accountOwnerId: string;
  accountOwnerName: string;
  accountType: AccountTypeOption;
  annualRevenue: number;
  employeeCount: number;
  tags: string[];
  notes: string;
  status: string;
  /** True when hub row has deleted_at set (archived). */
  isArchived: boolean;
  /** Linked public.accounts row for SI enrichment and RevOps timeline (sales_account_id bridge). */
  salesAccountId?: string | null;
  createdAtISO: string;
  updatedAtISO: string;
};

function pickAccountType(status: string | undefined): AccountTypeOption {
  const s = String(status ?? '').toLowerCase();
  if (s === 'partner') return 'partner';
  if (s === 'prospect') return 'prospect';
  return 'customer';
}

/** Empty account row for create flow (same shape as `accountToModel`). */
export function blankAccountDrawerModel(): AccountDrawerModel {
  const owner = MOCK_SCHEDULE_OWNERS[0]!;
  const now = new Date().toISOString();
  return {
    id: CRM_NEW_ACCOUNT_ID,
    name: '',
    industry: '',
    website: '',
    phone: '',
    street: '',
    city: '',
    stateOrCounty: '',
    country: '',
    postcode: '',
    territoryId: '',
    accountOwnerId: owner.id,
    accountOwnerName: owner.name,
    accountType: 'prospect',
    annualRevenue: 0,
    employeeCount: 0,
    tags: [],
    notes: '',
    status: 'active',
    isArchived: false,
    createdAtISO: now,
    updatedAtISO: now,
  };
}

function resolveOwner(ownerId: string | null | undefined, fallbackSeed: string) {
  const oid = ownerId?.trim() || MOCK_SCHEDULE_OWNERS[fallbackSeed.length % MOCK_SCHEDULE_OWNERS.length]!.id;
  const owner = MOCK_SCHEDULE_OWNERS.find((x) => x.id === oid) ?? MOCK_SCHEDULE_OWNERS[0]!;
  return { accountOwnerId: oid, accountOwnerName: owner.name };
}

function crmRowToModel(row: CrmHubAccount): AccountDrawerModel {
  const owners = resolveOwner(row.ownerId, row.id);
  const territoryId = row.territoryId?.trim() ?? '';
  return {
    id: row.id,
    name: row.name ?? '',
    industry: row.industry ?? '',
    website: row.website ?? '',
    phone: '',
    street: '',
    city: '',
    stateOrCounty: '',
    country: '',
    postcode: '',
    territoryId,
    accountOwnerId: owners.accountOwnerId,
    accountOwnerName: owners.accountOwnerName,
    accountType: pickAccountType(row.status),
    annualRevenue: 0,
    employeeCount:
      typeof row.employee_count === 'number' && Number.isFinite(row.employee_count)
        ? row.employee_count
        : 0,
    tags: [],
    notes: row.notes ?? '',
    status: row.status ?? 'active',
    isArchived: Boolean(row.isArchived),
    salesAccountId: row.salesAccountId ?? null,
    createdAtISO: row.created_at ?? new Date().toISOString(),
    updatedAtISO: row.updated_at ?? row.created_at ?? new Date().toISOString(),
  };
}

function accountToModel(a: CrmHubAccount): AccountDrawerModel {
  return crmRowToModel(a);
}

export type AccountDrawerPatch = Partial<
  Pick<
    AccountDrawerModel,
    | 'name'
    | 'industry'
    | 'website'
    | 'phone'
    | 'street'
    | 'city'
    | 'stateOrCounty'
    | 'country'
    | 'postcode'
    | 'territoryId'
    | 'accountOwnerId'
    | 'accountType'
    | 'annualRevenue'
    | 'employeeCount'
    | 'tags'
    | 'notes'
    | 'status'
  >
>;

export type UseAccountDrawerResult = {
  account: AccountDrawerModel | null;
  loading: boolean;
  error: Error | null;
  saveField: (field: string, value: unknown) => Promise<void>;
  /** Applies multiple field updates sequentially (same persistence contract as `saveField`). */
  patchAccount: (patch: AccountDrawerPatch) => Promise<void>;
  reloadAccount: () => void;
};

export function useAccountDrawer(
  accountId: string | null,
  onSave: (id: string, field: string, value: unknown) => Promise<void>
): UseAccountDrawerResult {
  const [account, setAccount] = useState<AccountDrawerModel | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const loadGenRef = useRef(0);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!accountId) {
      setAccount(null);
      setLoading(false);
      setError(null);
      return;
    }
    if (accountId === CRM_NEW_ACCOUNT_ID) {
      loadGenRef.current += 1;
      setAccount(blankAccountDrawerModel());
      setLoading(false);
      setError(null);
      return;
    }
    const gen = ++loadGenRef.current;
    setLoading(true);
    setError(null);
    let cancelled = false;
    void (async () => {
      try {
        const row = await crmGet<CrmHubAccount>('accounts', accountId);
        if (cancelled || loadGenRef.current !== gen) return;
        setAccount(accountToModel(row));
        setError(null);
      } catch (e) {
        if (cancelled || loadGenRef.current !== gen) return;
        setAccount(null);
        setError(e instanceof Error ? e : new Error('Failed to load account.'));
      } finally {
        if (!cancelled && loadGenRef.current === gen) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accountId, reloadToken]);

  const saveField = useCallback(
    async (field: string, value: unknown) => {
      const id = account?.id;
      if (!id) return;
      await onSave(id, field, value);
      setAccount((prev) => {
        if (!prev || prev.id !== id) return prev;
        const next: AccountDrawerModel = { ...prev };
        switch (field) {
          case 'tags':
            next.tags = Array.isArray(value) ? [...(value as string[])] : next.tags;
            break;
          case 'accountOwnerId': {
            const oid = String(value);
            next.accountOwnerId = oid;
            const o = MOCK_SCHEDULE_OWNERS.find((x) => x.id === oid);
            if (o) next.accountOwnerName = o.name;
            break;
          }
          case 'name':
            next.name = String(value).trim();
            break;
          case 'industry':
            next.industry = String(value).trim();
            break;
          case 'website':
            next.website = String(value).trim();
            break;
          case 'phone':
            next.phone = String(value).trim();
            break;
          case 'street':
            next.street = String(value).trim();
            break;
          case 'city':
            next.city = String(value).trim();
            break;
          case 'stateOrCounty':
            next.stateOrCounty = String(value).trim();
            break;
          case 'country':
            next.country = String(value).trim();
            break;
          case 'postcode':
            next.postcode = String(value).trim();
            break;
          case 'territoryId':
            next.territoryId = String(value);
            break;
          case 'accountType':
            next.accountType =
              value === 'partner' || value === 'prospect' || value === 'customer' ? value : next.accountType;
            break;
          case 'annualRevenue': {
            const n = typeof value === 'number' ? value : Number(value);
            next.annualRevenue = Number.isFinite(n) && n >= 0 ? n : 0;
            break;
          }
          case 'employeeCount': {
            const n = typeof value === 'number' ? value : Number(value);
            next.employeeCount = Number.isFinite(n) && n >= 0 ? Math.round(n) : 0;
            break;
          }
          case 'notes':
            next.notes = String(value).trim();
            break;
          case 'status':
            next.status = String(value).trim() || next.status;
            break;
          default:
            break;
        }
        return next;
      });
    },
    [account?.id, onSave]
  );

  const patchAccount = useCallback(
    async (patch: AccountDrawerPatch) => {
      if (!account?.id) return;
      for (const [field, value] of Object.entries(patch) as [keyof AccountDrawerPatch, unknown][]) {
        if (value === undefined) continue;
        await saveField(field as string, value);
      }
    },
    [account?.id, saveField]
  );

  return { account, loading, error, saveField, patchAccount, reloadAccount: () => setReloadToken((n) => n + 1) };
}
